import AutorenewIcon from "@mui/icons-material/Autorenew";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockIcon from "@mui/icons-material/Lock";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Dialog from "@mui/material/Dialog";
import { useCallback, useId } from "react";
import type { PaymentMethod } from "@/interfaces/paymentMethod";
import type { PostpaidLaunchInput } from "@/interfaces/review";
import { formatMoney } from "@/lib/campaigns";
import { cardBrandLabel } from "@/utils/payment-helper";
import PostpaidConfirm, { type PostpaidConfirmProps } from "./PostpaidConfirm";

/** Stripe's brand purple — a vendor mark, deliberately not a GfG design token. */
const STRIPE_PURPLE = "#635bff";

export interface ConfirmPayModalProps {
  /** Controlled by the checkout column, which owns the confirm/success sequence. */
  open: boolean;
  /** Charged now. Equals `total` unless pay-as-you-give splits the first instalment off. */
  dueToday: number;
  /** The full campaign budget — what the deposit and the later balance are read against. */
  total: number;
  /**
   * What could still reach the card as gifts are redeemed.
   *
   * NOT the whole remaining 60%: any credit left in the wallet is drawn down before the card, so
   * this is the later balance after that. Ignored unless pay-as-you-give is on.
   */
  potentialCardSpend?: number;
  /** Pay-as-you-give switch state — reframes the amount line and reveals the recharge note. */
  payAsYouGive: boolean;
  /** The organisation's pay-as-you-give deposit percentage — what the amount line and the note say. */
  minUpfrontPc: number;
  /** The whole campaign budget, the deposit and its minimum — so a raised deposit can say so. */
  budget?: number;
  deposit?: number;
  minDeposit?: number;
  creditsApplied?: number;
  /** The group's saved card, or null when none is on file yet. */
  card: PaymentMethod | null;
  /**
   * The credits launch will buy on this card. Zero when the wallet already covers the campaign
   * and nothing is charged today.
   */
  creditsToBuy: number;
  /**
   * The launch mutation is in flight (`useLaunchCampaign().isPending`, owned by the checkout
   * column). Swaps the CTA for the spinner and makes the whole dialog undismissable.
   */
  processing: boolean;
  /** Failure copy to show above the CTA, or null when the last attempt didn't fail. */
  errorMessage?: string | null;
  onCancel: () => void;
  /**
   * Fires the charge. The parent owns the mutation; this button only asks for it. A postpaid
   * confirm passes what the client chose and accepted.
   */
  onConfirm: (postpaid?: PostpaidLaunchInput) => void;
  /** Set for a postpaid campaign — the dialog then shows the deposit, its terms and the acceptance. */
  postpaid?: Omit<
    PostpaidConfirmProps,
    "card" | "processing" | "errorMessage" | "onCancel" | "onConfirm"
  >;
}

/** "Exp 12/27", or null when Stripe hasn't reported an expiry for the card yet. */
function formatExpiry(month: number | null, year: number | null): string | null {
  if (month === null || year === null) return null;
  return `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`;
}

/**
 * The "Confirm & pay" step, opened by the checkout column once a payment method is
 * on file. Mirrors the prototype's `#campaignCheckoutOverlay`
 * (Campaign-Builder-Master-Prototype.html, markup ~line 10715, JS
 * `openCampaignCheckout()` / `_updateCampaignPayBtn()` / `processCampaignPayment()`).
 *
 * Presentation only: no Stripe call is made here, and no timers either. `onConfirm`
 * fires the checkout column's `useLaunchCampaign()` mutation, and `processing` /
 * `errorMessage` are that mutation's state passed back down. The prototype's 1.6s
 * beat now lives inside the mutation, where the real request will replace it.
 *
 * Built on MUI `Dialog` — the same shell every other bespoke modal in this app uses
 * (`SendTestModal`, `BulkUploadModal`, `usersOrgs/AddPaymentMethodModal`) — so the
 * backdrop, focus trap, Escape handling and scroll lock come from one place. It does
 * not go through `GfgModal`: that shell is a fixed icon/title/description/two-button
 * layout with no room for the amount breakdown or the processing state.
 *
 * NO TERMS CLICKWRAP for prepaid and pay-as-you-give. It was here and was removed (Jayanth,
 * 2026-09-23): the acceptance that binds the organization is the one recorded at signup, against a
 * version, in `legal_acceptances` — so a second tick per campaign gated the charge on a box that was
 * never recorded anywhere. POSTPAID is different: it commits the client to a later invoice on terms
 * specific to this campaign, and its acceptance IS recorded — user, time, terms version and the
 * label exactly as shown — on `campaign_budget_postpaid_terms` (see `PostpaidConfirm`).
 */
export default function ConfirmPayModal({
  open,
  dueToday,
  total,
  potentialCardSpend = 0,
  payAsYouGive,
  minUpfrontPc,
  budget = 0,
  deposit,
  minDeposit,
  creditsApplied = 0,
  card,
  creditsToBuy,
  processing,
  errorMessage,
  onCancel,
  onConfirm,
  postpaid,
}: ConfirmPayModalProps) {
  const titleId = useId();

  // Dismissal is inert while processing — backdrop click and Escape both route here, so the
  // request can't be abandoned half-way through a charge the server may already have taken.
  const handleClose = useCallback(() => {
    if (!processing) onCancel();
  }, [processing, onCancel]);

  // Payment is satisfiable when a card is on file, or when credits already cover
  // everything owed and there is nothing to charge (prototype `_updateCampaignPayBtn`).
  const canPay = card !== null || dueToday === 0;
  const payDisabled = !canPay || processing;
  const payLabel = canPay ? `Pay ${formatMoney(dueToday)}` : "Add a payment method to continue";
  const expiry = card ? formatExpiry(card.expMonth, card.expYear) : null;
  // The client chose to pay more than the minimum at Step 4: the line names the percentage they
  // are actually paying, and the minimum they went above, so the figure they confirm is theirs.
  const raised = deposit != null && minDeposit != null && deposit > minDeposit;
  const depositPc =
    raised && budget > 0 ? Math.round(((deposit as number) / budget) * 100) : minUpfrontPc;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            m: 2,
            width: 600,
            maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        },
      }}
    >
      <h2
        id={titleId}
        className="mb-5 text-left font-crimson font-normal text-2xl text-rodeo-black"
      >
        Confirm &amp; pay
      </h2>

      {postpaid ? (
        <PostpaidConfirm
          {...postpaid}
          card={card}
          processing={processing}
          errorMessage={errorMessage}
          onCancel={onCancel}
          onConfirm={(input) => onConfirm(input)}
        />
      ) : (
        <>
          {/* The rule under the title is this row's top border — one amount line only. */}
          <div className="flex items-baseline justify-between border-smoke border-t pt-3.5 font-lato text-[15px] text-graphite">
            <span>{payAsYouGive ? `Due today (${depositPc}% deposit)` : "Total due"}</span>
            <span className="font-crimson font-normal text-[26px] text-rodeo-black">
              {formatMoney(dueToday)}
            </span>
          </div>
          {payAsYouGive && raised && (
            <p className="mt-1 mb-0 font-lato text-[12px] text-graphite">
              {formatMoney(deposit as number)} deposit, above the{" "}
              {formatMoney(minDeposit as number)} ({minUpfrontPc}%) minimum
              {creditsApplied > 0
                ? `, less ${formatMoney(creditsApplied)} of your existing credits`
                : ""}
              .
            </p>
          )}

          {/* WHAT THE MONEY IS FOR. The card never pays for a campaign — it buys credits, and credits
          fund the campaign. A buyer told only "Pay $500" reasonably reads it as paying for gifts,
          then finds 500 credits in a wallet they did not ask for.

          NO SECOND MONEY FIGURE here. It used to say "charge {dueToday} to buy {creditsToBuy}
          credits", which cannot both be true at 1 credit = $1: launch buys `roundUpToIncrement` of
          what the wallet is short, so the card is charged for the ROUNDED credits, up to $4.99 above
          `dueToday`. The credits are the honest half of that sentence, so only they are stated.

          The claim that wallet credits are spent first is gone too: with the apply-credits box
          unticked the wallet is deliberately left alone and the card buys the whole requirement. */}
          {creditsToBuy > 0 ? (
            <div className="mt-3.5 flex items-start gap-2 rounded-lg bg-light-dust px-3.5 py-3 font-lato text-graphite text-xs leading-[1.5]">
              <ShoppingCartOutlinedIcon
                aria-hidden="true"
                className="mt-px shrink-0 text-dark-dust"
                sx={{ fontSize: 16 }}
              />
              <span>
                Your card buys{" "}
                <strong className="font-bold text-rodeo-black">
                  {creditsToBuy.toLocaleString()} credits
                </strong>
                , which fund this campaign. If it is declined, nothing is charged.
              </span>
            </div>
          ) : (
            <div className="mt-3.5 rounded-lg bg-light-dust px-3.5 py-3 font-lato text-graphite text-xs leading-[1.5]">
              Your wallet covers this campaign in full &mdash; nothing is charged today.
            </div>
          )}

          {card && (
            <div className="my-4.5 flex items-center gap-2.5 rounded-lg border border-smoke px-3.5 py-3">
              <span className="rounded-sm bg-rodeo-black px-1.75 py-0.75 font-extrabold font-lato text-[10px] text-white uppercase tracking-[0.5px]">
                {cardBrandLabel(card.brand)}
              </span>
              <span className="font-lato font-semibold text-rodeo-black text-sm">
                •••• {card.last4 ?? "••••"}
              </span>
              {expiry && (
                <span className="ml-auto font-lato text-graphite text-xs">Exp {expiry}</span>
              )}
            </div>
          )}

          {payAsYouGive && (
            <div className="mb-4.5 flex items-start gap-2 rounded-lg bg-light-dust px-3.5 py-3 font-lato text-graphite text-xs leading-[1.5]">
              <AutorenewIcon
                aria-hidden="true"
                className="mt-px shrink-0 text-dark-dust"
                sx={{ fontSize: 16 }}
              />
              {/* Deliberately NOT "to cover your full budget". The model never charges the rest
              unless gifts are actually claimed — if redemptions stop, the client stops paying,
              which is the point of pay-as-you-give rather than a caveat to it. Promising the full
              amount here would describe a commitment the system does not make.

              The figure quoted is what could reach the CARD, which is the later balance after any
              remaining wallet credit is drawn down — not the raw 60%. */}
              <span>
                Pay-as-you-give:{" "}
                <strong className="font-bold text-rodeo-black">{formatMoney(dueToday)}</strong>{" "}
                billed today, a {depositPc}% deposit on your{" "}
                <strong className="font-bold text-rodeo-black">{formatMoney(total)}</strong>{" "}
                campaign budget. As gifts are claimed we top up automatically — up to{" "}
                <strong className="font-bold text-rodeo-black">
                  {formatMoney(potentialCardSpend)}
                </strong>{" "}
                more on this card. You only pay for what is claimed.
              </span>
            </div>
          )}

          {/* Same treatment as the expiry acknowledgment's inline error, so a failed charge and a
          missed checkbox don't look like two different kinds of problem. */}
          {errorMessage && (
            <p
              role="alert"
              className="mt-0 mb-3 flex items-center gap-1.5 font-lato font-semibold text-[13px] text-good-wine leading-snug"
            >
              <ErrorOutlineIcon className="shrink-0" sx={{ fontSize: 17 }} aria-hidden="true" />
              {errorMessage}
            </p>
          )}

          <button
            type="button"
            disabled={payDisabled}
            aria-busy={processing}
            onClick={() => onConfirm()}
            className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-rodeo-black py-3.5 font-bold font-lato text-sm text-white tracking-[0.5px] transition-colors hover:bg-[#222] disabled:cursor-default disabled:opacity-85 disabled:hover:bg-rodeo-black"
          >
            {processing ? (
              <>
                <span
                  aria-hidden="true"
                  className="h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white"
                />
                Processing…
              </>
            ) : (
              <>
                <LockIcon aria-hidden="true" sx={{ fontSize: 16 }} />
                {payLabel}
              </>
            )}
          </button>

          <button
            type="button"
            disabled={processing}
            onClick={onCancel}
            className="mt-2.5 w-full cursor-pointer py-1.5 font-bold font-lato text-graphite text-xs uppercase tracking-[1px] hover:text-rodeo-black disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
        </>
      )}

      <div className="mt-3.5 flex items-center justify-center gap-1.5 font-lato text-graphite text-xs">
        <LockIcon aria-hidden="true" sx={{ fontSize: 13 }} />
        Powered by{" "}
        <strong className="font-extrabold tracking-[-0.2px]" style={{ color: STRIPE_PURPLE }}>
          stripe
        </strong>
      </div>
    </Dialog>
  );
}
